import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import View from "react-native-web/dist/exports/View";
import RadioInput from './RadioInput';
import { applyOuterBorder, applyShadowToken, resolveThemeTokens, validateThemeTokens, useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensSetPropType, selectSystemProps, selectTokens, viewProps } from '../utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);

// TODO: roll out a standard approach to token sets
// https://github.com/telus/universal-design-system/issues/782
const tokenKeys = ['checkedBackgroundColor', 'checkedSize', 'inputBackgroundColor', 'inputBorderColor', 'inputBorderWidth', 'inputOutlineColor', 'inputOutlineWidth', 'inputSize', 'outerBorderColor', 'outerBorderWidth', 'outerBorderGap', 'inputSize'];
export const selectRadioButtonTokens = (themeTokens, prefix) => selectTokens(tokenKeys, themeTokens, prefix);
const getBorderRadius = inputSize => inputSize / 2;
const selectCheckedStyles = _ref => {
  let {
    checkedBackgroundColor,
    checkedSize
  } = _ref;
  return {
    backgroundColor: checkedBackgroundColor,
    borderRadius: checkedSize / 2,
    height: checkedSize,
    width: checkedSize
  };
};
const selectIconTokens = _ref2 => {
  let {
    icon,
    iconColor,
    checkedSize
  } = _ref2;
  return {
    icon,
    color: iconColor,
    size: checkedSize
  };
};
const selectInputIconStyles = (_ref3, isChecked) => {
  let {
    iconBackgroundColor,
    inputBackgroundColor,
    inputBorderColor,
    inputBorderWidth,
    inputSize,
    containerShadow
  } = _ref3;
  return {
    borderColor: inputBorderColor,
    borderRadius: getBorderRadius(inputSize),
    borderWidth: inputBorderWidth,
    backgroundColor: isChecked ? iconBackgroundColor : inputBackgroundColor,
    height: inputSize,
    width: inputSize,
    ...applyShadowToken(containerShadow)
  };
};
const selectInputStyles = _ref4 => {
  let {
    inputBackgroundColor,
    inputBorderColor,
    inputBorderWidth,
    inputOutlineColor,
    inputOutlineWidth,
    inputSize
  } = _ref4;
  return {
    borderColor: inputBorderColor,
    borderRadius: getBorderRadius(inputSize),
    borderWidth: inputBorderWidth,
    backgroundColor: inputBackgroundColor,
    height: inputSize,
    width: inputSize,
    ...Platform.select({
      web: {
        outlineStyle: 'solid',
        outlineColor: inputOutlineColor,
        outlineWidth: inputOutlineWidth
      }
    })
  };
};
const selectOuterBorderStyles = _ref5 => {
  let {
    outerBorderColor,
    outerBorderWidth,
    outerBorderGap,
    inputSize
  } = _ref5;
  return {
    ...applyOuterBorder({
      outerBorderColor,
      outerBorderWidth,
      outerBorderGap,
      borderRadius: getBorderRadius(inputSize)
    })
  };
};

/**
 * The visual toggle of a radio input. Is not interactive on its own, should be used inside
 * an interactive parent that passes down props when interacted with.
 */
const RadioButton = /*#__PURE__*/React.forwardRef((_ref6, ref) => {
  let {
    isChecked,
    tokens,
    inactive,
    defaultChecked,
    inputId,
    isControlled,
    handleChange,
    name: inputName,
    value,
    testID = 'Radio-Checked',
    ...rest
  } = _ref6;
  const radioTokens = useThemeTokens('Radio', tokens, {
    checked: isChecked
  });
  const {
    icon: IconComponent,
    ...iconTokens
  } = selectIconTokens(radioTokens);
  const themeTokens = validateThemeTokens(resolveThemeTokens(IconComponent ? radioTokens : tokens, {
    checked: isChecked
  }), getTokensSetPropType(tokenKeys), 'RadioButton');
  const getCheckedInput = checked => {
    if (!checked) return null;
    if (!IconComponent) {
      return /*#__PURE__*/_jsx(View, {
        style: selectCheckedStyles(themeTokens),
        testID: testID
      });
    }
    return /*#__PURE__*/_jsx(IconComponent, {
      ...iconTokens
    });
  };
  return /*#__PURE__*/_jsx(View, {
    style: selectOuterBorderStyles(themeTokens),
    ...selectProps(rest),
    children: /*#__PURE__*/_jsxs(View, {
      style: [staticStyles.defaultInputStyles, IconComponent ? selectInputIconStyles(themeTokens, isChecked) : selectInputStyles(themeTokens)],
      testID: "Radio-Input",
      children: [/*#__PURE__*/_jsx(RadioInput, {
        ref: ref,
        checked: isChecked,
        defaultChecked: defaultChecked,
        disabled: inactive,
        id: inputId,
        isControlled: isControlled,
        onChange: handleChange,
        name: inputName,
        value: value
      }), getCheckedInput(isChecked)]
    })
  });
});
RadioButton.displayName = 'RadioButton';
RadioButton.propTypes = {
  ...selectedSystemPropTypes,
  isChecked: PropTypes.bool,
  tokens: getTokensSetPropType(tokenKeys, {
    allowFunction: true
  }),
  inactive: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  inputId: PropTypes.string,
  isControlled: PropTypes.bool,
  handleChange: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.bool
};
const staticStyles = StyleSheet.create({
  defaultInputStyles: {
    alignItems: 'center',
    justifyContent: 'center'
  }
});
export default RadioButton;